import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import { ARGENTINA_COUNTRY_CODE, CLICK_TO_PAY_PAYMENT_METHOD } from "@repo/ui/constants/payment";

import { useAppSelector } from "../hooks";

// Payment Method Types
export type PaymentMethodType =
  | "credit-debit"
  | "gift-card"
  | "bank-transfer"
  | "paypal"
  | "applepay"
  | "mercadopago"
  | "tenpo"
  | "card"
  | "niubiz_qr"
  | "google_pay"
  | "click_to_pay"
  | "wallet";

// Document Types
export type DocumentType = "TNI" | "RUT" | "PAS";

// Country Types
export type CountryType = "peru" | "chile" | "colombia" | "argentina" | "mexico";

export const countryOptions = [
  { value: "peru", label: "Perú (+51)", prefix: "51" },
  { value: "chile", label: "Chile (+56)", prefix: "56" },
  { value: "colombia", label: "Colombia (+57)", prefix: "57" },
  { value: "argentina", label: "Argentina (+54)", prefix: "54" },
  { value: "mexico", label: "México (+52)", prefix: "52" },
];

export const availablePrefix = [...countryOptions].map((c) => c.prefix);

// Contact Information
export interface ContactInfo {
  isFirstPassenger: boolean;
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  phoneNumber: string;
  phoneNumberPrefix: string | null;
  additionalEmail?: string;
}

// Credit/Debit Card Information
export interface CreditCardInfo {
  cardNumber: string;
  expirationDate: string;
  cvv: string;
  cardholderName: string;
  documentType: DocumentType;
  documentNumber: string;
  installments: string;
}

// Colombia: NIT/Cédula/Pasaporte. DR: RNC/Cédula/Pasaporte.
export type InvoiceIdentifierType = "NIT" | "RNC" | "CEDULA" | "PASAPORTE" | "";

// Billing Information (for invoices)
export interface BillingInfo {
  rut: string;
  companyName: string;
  address: string;
  commune: string;
  city: string;
  giro: string;
  email: string;
  idCostCenter?: string;
  // `string`, not `InvoiceIdentifierType` — round-trips through the generic
  // `updateBillingInfo(values)` dispatch; the Select UI constrains the actual values.
  identifierType?: string;
  // DR only: RNC/Cédula/Pasaporte value → Navitaire's Mobile field. Colombia reuses `rut`.
  identifier?: string;
}

// Document Selection
export interface DocumentSelection {
  receiptChecked: boolean;
  invoiceChecked: boolean;
}

// Tax Information (Argentina RG 5617 - CUIT/CUIL capture)
export interface TaxInformation {
  countryOfIssuanceCode: string;
  cuitCuil: string;
}

// Terms and Conditions
export interface TermsAndConditions {
  accepted: boolean;
}

// Gift Card Information
export interface GiftCardInfo {
  code: string;
  isAdded: boolean;
  isApplied: boolean;
  redeemableAmount?: number | null;
  availableAmount?: number | null;
  appliedAmount?: number | null;
  remainingVoucherBalance?: number | null;
  currencyCode?: string | null;
}

// Gift Card Notification State
export interface GiftCardNotification {
  showSuccess: boolean;
  showError: boolean;
  showPartialPayment: boolean;
}

// Wallet Information
export interface WalletInfo {
  appliedAmountInLocalCurrency: number | null;
  walletIsApplied: boolean;
  isWalletFullPayment: boolean;
}

// Click to Pay Deferred Mount State
export type ClickToPayPhase = "idle" | "preparing" | "awaiting-customer" | "creating-payment";

export interface ClickToPayMethod {
  type: string;
  vaultedToken?: string;
  optionId: string;
}

// Payment State
export interface PaymentState {
  contactInfo: ContactInfo;
  paymentMethod: PaymentMethodType | null;
  creditCardInfo: CreditCardInfo;
  billingInfo: BillingInfo;
  documentSelection: DocumentSelection;
  taxInformation: TaxInformation;
  termsAndConditions: TermsAndConditions;
  giftCardInfo: GiftCardInfo;
  confirmed: boolean;
  // UI/Error helpers
  contactErrorMessage?: string | null;
  pnr: string | null;
  // True when the payment page was reached from the itinerary's declined-payment retry modal
  isPaymentRetry: boolean;
  shouldShowPaymentDetails: boolean;
  yunoPaymentStatus?: string | null;
  showFinalizationModal: boolean;
  // Gift Card Payment State
  isGiftCardFullPayment: boolean;
  newBalanceDue: number | null;
  hasUsedGiftCardInReservation: boolean;
  // Gift Card Notification
  giftCardNotification: GiftCardNotification;
  // Wallet Payment State
  walletInfo: WalletInfo;
  // Click to Pay Deferred Mount State
  clickToPayPhase: ClickToPayPhase;
  clickToPayMethod: ClickToPayMethod | null;
  // Live validity of BillingForm's culture-aware Zod schema, dispatched reactively
  // from BillingForm itself - not duplicated here since this slice has no locale.
  isBillingFormValid: boolean;
}

// Initial Contact Info
const initialContactInfo: ContactInfo = {
  isFirstPassenger: true,
  firstName: "",
  lastName: "",
  email: "",
  country: "",
  phoneNumber: "",
  phoneNumberPrefix: "",
  additionalEmail: "",
};

// Initial Credit Card Info
const initialCreditCardInfo: CreditCardInfo = {
  cardNumber: "",
  expirationDate: "",
  cvv: "",
  cardholderName: "",
  documentType: "TNI",
  documentNumber: "",
  installments: "",
};

// Initial Billing Info
const initialBillingInfo: BillingInfo = {
  rut: "",
  companyName: "",
  address: "",
  commune: "",
  city: "",
  giro: "",
  email: "",
  identifierType: "",
  identifier: "",
};

// Initial Document Selection
const initialDocumentSelection: DocumentSelection = {
  receiptChecked: false,
  invoiceChecked: false,
};

// Initial Tax Information
const initialTaxInformation: TaxInformation = {
  countryOfIssuanceCode: ARGENTINA_COUNTRY_CODE,
  cuitCuil: "",
};

// Initial Terms and Conditions
const initialTermsAndConditions: TermsAndConditions = {
  accepted: false,
};

// Initial Gift Card Info
const initialGiftCardInfo: GiftCardInfo = {
  code: "",
  isAdded: false,
  isApplied: false,
  redeemableAmount: null,
  availableAmount: null,
  appliedAmount: null,
  remainingVoucherBalance: null,
  currencyCode: null,
};

// Initial Gift Card Notification
const initialGiftCardNotification: GiftCardNotification = {
  showSuccess: false,
  showError: false,
  showPartialPayment: false,
};

// Initial Wallet Info
const initialWalletInfo: WalletInfo = {
  appliedAmountInLocalCurrency: null,
  walletIsApplied: false,
  isWalletFullPayment: false,
};

const initialState: PaymentState = {
  contactInfo: initialContactInfo,
  paymentMethod: null,
  creditCardInfo: initialCreditCardInfo,
  billingInfo: initialBillingInfo,
  documentSelection: initialDocumentSelection,
  taxInformation: initialTaxInformation,
  termsAndConditions: initialTermsAndConditions,
  giftCardInfo: initialGiftCardInfo,
  confirmed: false,
  contactErrorMessage: null,
  pnr: null,
  isPaymentRetry: false,
  shouldShowPaymentDetails: false,
  yunoPaymentStatus: null,
  showFinalizationModal: false,
  // Gift Card Payment State
  isGiftCardFullPayment: false,
  newBalanceDue: null,
  hasUsedGiftCardInReservation: false,
  giftCardNotification: initialGiftCardNotification,
  // Wallet Payment State
  walletInfo: initialWalletInfo,
  // Click to Pay Deferred Mount State
  clickToPayPhase: "idle",
  clickToPayMethod: null,
  isBillingFormValid: true,
};

// Validation functions
const validateContactInfo = (contactInfo: ContactInfo): boolean => {
  return (
    contactInfo.firstName.trim() !== "" &&
    contactInfo.lastName.trim() !== "" &&
    contactInfo.email.trim() !== "" &&
    contactInfo.phoneNumber.trim() !== ""
    // && availablePrefix.includes(contactInfo.phoneNumberPrefix ?? "")
  );
};

const validateCreditCardInfo = (creditCardInfo: CreditCardInfo): boolean => {
  return (
    creditCardInfo.cardNumber.trim() !== "" &&
    creditCardInfo.expirationDate.trim() !== "" &&
    creditCardInfo.cvv.trim() !== "" &&
    creditCardInfo.cardholderName.trim() !== "" &&
    creditCardInfo.documentNumber.trim() !== ""
  );
};

// commune/giro are intentionally excluded — Colombia's Invoice form doesn't collect them,
// and this check has no access to locale to know which culture's rules apply. The
// culture-aware requiredness lives in BillingForm's own schema.
const validateBillingInfo = (billingInfo: BillingInfo, invoiceChecked: boolean): boolean => {
  if (!invoiceChecked) return true; // Not required if invoice not checked
  return (
    billingInfo.rut.trim() !== "" &&
    billingInfo.companyName.trim() !== "" &&
    billingInfo.address.trim() !== "" &&
    billingInfo.city.trim() !== "" &&
    billingInfo.email.trim() !== ""
  );
};

const validatePayment = (state: PaymentState): boolean => {
  const hasContactInfo = validateContactInfo(state.contactInfo);
  const hasPaymentMethod = state.paymentMethod !== null;
  const hasCreditCardInfo =
    state.paymentMethod === "credit-debit" ? validateCreditCardInfo(state.creditCardInfo) : true;
  const hasBillingInfo = validateBillingInfo(
    state.billingInfo,
    state.documentSelection.invoiceChecked,
  );
  const hasTermsAccepted = state.termsAndConditions.accepted;
  const hasDocumentSelection =
    state.documentSelection.receiptChecked || state.documentSelection.invoiceChecked;

  return (
    hasContactInfo &&
    hasPaymentMethod &&
    hasCreditCardInfo &&
    hasBillingInfo &&
    hasTermsAccepted &&
    hasDocumentSelection
  );
};

// WEB-4940: a Click to Pay mount lives in the page (Yuno SDK instance, YunoPayment refs), so the
// phase, the method it targets and the Click to Pay selection are only valid while the
// YunoPayment that mounted it is alive. Kept past that (reload, navigating away and back), they
// lock every other method and leave the row on a spinner nothing will resolve.
const releaseClickToPay = (state: PaymentState) => {
  state.clickToPayPhase = "idle";
  state.clickToPayMethod = null;
  if (state.paymentMethod === CLICK_TO_PAY_PAYMENT_METHOD) {
    state.paymentMethod = null;
    state.confirmed = validatePayment(state);
  }
};

const paymentSlice = createSlice({
  name: "payment",
  initialState,
  reducers: {
    // Contact Info Actions
    updateContactInfo: (state, action: PayloadAction<Partial<ContactInfo>>) => {
      state.contactInfo = { ...state.contactInfo, ...action.payload };
      state.confirmed = validatePayment(state);
      if (validateContactInfo(state.contactInfo)) {
        state.contactErrorMessage = null;
      }
    },
    setContactInfo: (state, action: PayloadAction<ContactInfo>) => {
      state.contactInfo = action.payload;
      state.confirmed = validatePayment(state);
      if (validateContactInfo(state.contactInfo)) {
        state.contactErrorMessage = null;
      }
    },

    // Payment Method Actions
    setPaymentMethod: (state, action: PayloadAction<PaymentMethodType | null>) => {
      state.paymentMethod = action.payload
        ? (action.payload.toLowerCase() as PaymentMethodType)
        : null;
      state.confirmed = validatePayment(state);
    },
    clearPaymentMethod: (state) => {
      state.paymentMethod = null;
      state.confirmed = validatePayment(state);
    },

    // Credit Card Actions
    updateCreditCardInfo: (state, action: PayloadAction<Partial<CreditCardInfo>>) => {
      state.creditCardInfo = { ...state.creditCardInfo, ...action.payload };
      state.confirmed = validatePayment(state);
    },
    setCreditCardInfo: (state, action: PayloadAction<CreditCardInfo>) => {
      state.creditCardInfo = action.payload;
      state.confirmed = validatePayment(state);
    },

    // Billing Info Actions
    updateBillingInfo: (state, action: PayloadAction<Partial<BillingInfo>>) => {
      state.billingInfo = { ...state.billingInfo, ...action.payload };
      state.confirmed = validatePayment(state);
    },
    setBillingInfo: (state, action: PayloadAction<BillingInfo>) => {
      state.billingInfo = action.payload;
      state.confirmed = validatePayment(state);
    },

    // Tax Information Actions (Argentina RG 5617 - CUIT/CUIL)
    updateTaxInformation: (state, action: PayloadAction<Partial<TaxInformation>>) => {
      state.taxInformation = { ...state.taxInformation, ...action.payload };
    },
    setTaxInformation: (state, action: PayloadAction<TaxInformation>) => {
      state.taxInformation = action.payload;
    },

    // Document Selection Actions
    setDocumentSelection: (state, action: PayloadAction<Partial<DocumentSelection>>) => {
      state.documentSelection = {
        ...state.documentSelection,
        ...action.payload,
      };
      state.confirmed = validatePayment(state);
    },
    setReceiptChecked: (state, action: PayloadAction<boolean>) => {
      state.documentSelection.receiptChecked = action.payload;
      if (action.payload) {
        state.documentSelection.invoiceChecked = false;
      }
      state.confirmed = validatePayment(state);
    },
    setInvoiceChecked: (state, action: PayloadAction<boolean>) => {
      state.documentSelection.invoiceChecked = action.payload;
      if (action.payload) {
        state.documentSelection.receiptChecked = false;
      }
      state.confirmed = validatePayment(state);
    },

    // Terms and Conditions Actions
    setTermsAccepted: (state, action: PayloadAction<boolean>) => {
      state.termsAndConditions.accepted = action.payload;
      state.confirmed = validatePayment(state);
    },

    // Contact info error helper (for UI coordination between sections)
    setContactErrorMessage: (state, action: PayloadAction<string | null>) => {
      state.contactErrorMessage = action.payload ?? null;
    },

    // Confirmation Actions
    confirmPayment: (state) => {
      state.confirmed = true;
    },
    resetPaymentConfirmation: (state) => {
      state.confirmed = false;
    },
    validatePaymentAction: (state) => {
      state.confirmed = validatePayment(state);
    },

    // Gift Card Actions
    setGiftCardCode: (state, action: PayloadAction<string>) => {
      state.giftCardInfo.code = action.payload;
      state.confirmed = validatePayment(state);
    },
    setGiftCardData: (
      state,
      action: PayloadAction<{
        code: string;
        redeemableAmount?: number;
        availableAmount?: number;
        currencyCode?: string | null;
      }>,
    ) => {
      const {
        code,
        redeemableAmount = null,
        availableAmount = null,
        currencyCode = null,
      } = action.payload;
      state.giftCardInfo.code = code;
      state.giftCardInfo.isAdded = true;
      state.giftCardInfo.redeemableAmount = redeemableAmount;
      state.giftCardInfo.availableAmount = availableAmount;
      state.giftCardInfo.currencyCode = currencyCode;
      state.confirmed = validatePayment(state);
    },
    toggleGiftCard: (state, action: PayloadAction<string>) => {
      const code = action.payload;

      // Ensure giftCardInfo is properly initialized
      if (!state.giftCardInfo) {
        state.giftCardInfo = { ...initialGiftCardInfo };
      }

      if (state.giftCardInfo.isAdded && state.giftCardInfo.code === code) {
        // Remove gift card if it's the same code
        state.giftCardInfo = { ...initialGiftCardInfo };
      } else {
        // Add new gift card
        state.giftCardInfo.code = code;
        state.giftCardInfo.isAdded = true;
        // Reset API-driven fields until fetched
        state.giftCardInfo.redeemableAmount = null;
        state.giftCardInfo.availableAmount = null;
        state.giftCardInfo.currencyCode = null;
      }

      state.confirmed = validatePayment(state);
    },
    removeGiftCard: (state) => {
      state.giftCardInfo = initialGiftCardInfo;
      state.isGiftCardFullPayment = false;
      state.newBalanceDue = null;
      state.confirmed = validatePayment(state);
    },

    // Apply Gift Card to Booking
    applyGiftCardPayment: (
      state,
      action: PayloadAction<{
        appliedAmount: number;
        newBalanceDue: number;
        remainingVoucherBalance?: number;
        isFullPayment: boolean;
      }>,
    ) => {
      const { appliedAmount, newBalanceDue, remainingVoucherBalance, isFullPayment } =
        action.payload;
      const safeAppliedAmount = Math.max(0, appliedAmount);
      const safeNewBalanceDue = Math.max(0, newBalanceDue);

      state.giftCardInfo.isApplied = true;
      state.giftCardInfo.appliedAmount = safeAppliedAmount;
      state.giftCardInfo.remainingVoucherBalance = remainingVoucherBalance ?? null;
      state.isGiftCardFullPayment = isFullPayment;
      state.newBalanceDue = safeNewBalanceDue;
      state.hasUsedGiftCardInReservation = true;

      if (isFullPayment) {
        state.paymentMethod = "gift-card";
      }

      state.confirmed = validatePayment(state);
    },

    // Set one-time use restriction
    setHasUsedGiftCardInReservation: (state, action: PayloadAction<boolean>) => {
      state.hasUsedGiftCardInReservation = action.payload;
    },

    // Reset Actions
    resetPayment: (state) => {
      state.contactInfo = initialContactInfo;
      state.paymentMethod = null;
      state.creditCardInfo = initialCreditCardInfo;
      state.billingInfo = initialBillingInfo;
      state.documentSelection = initialDocumentSelection;
      state.taxInformation = initialTaxInformation;
      state.termsAndConditions = initialTermsAndConditions;
      state.giftCardInfo = initialGiftCardInfo;
      state.confirmed = false;
      state.contactErrorMessage = null;
      state.isGiftCardFullPayment = false;
      state.newBalanceDue = null;
      state.walletInfo = initialWalletInfo;
      state.clickToPayPhase = "idle";
      state.clickToPayMethod = null;
      state.isBillingFormValid = true;
      // Keep hasUsedGiftCardInReservation - don't reset
    },

    setPNR: (state, action: PayloadAction<string>) => {
      state.pnr = action.payload;
      state.isPaymentRetry = false;
    },
    setIsPaymentRetry: (state, action: PayloadAction<boolean>) => {
      state.isPaymentRetry = action.payload;
    },

    setShouldShowPaymentDetails: (state, action: PayloadAction<boolean>) => {
      state.shouldShowPaymentDetails = action.payload;
    },
    setYunoPaymentStatus: (state, action: PayloadAction<string | null>) => {
      state.yunoPaymentStatus = action.payload;
    },
    setShowFinalizationModal: (state, action: PayloadAction<boolean>) => {
      state.showFinalizationModal = action.payload;
    },

    // Wallet Actions
    setAppliedWalletAmountInLocalCurrency: (state, action: PayloadAction<number | null>) => {
      state.walletInfo.appliedAmountInLocalCurrency = action.payload;
    },
    setWalletIsApplied: (state, action: PayloadAction<boolean>) => {
      state.walletInfo.walletIsApplied = action.payload;
    },
    setWalletFullPayment: (state, action: PayloadAction<boolean>) => {
      state.walletInfo.isWalletFullPayment = action.payload;
    },
    setNewBalanceDue: (state, action: PayloadAction<number | null>) => {
      state.newBalanceDue = action.payload !== null ? Math.max(0, action.payload) : null;
    },

    // Click to Pay Deferred Mount Actions
    setClickToPayPhase: (state, action: PayloadAction<ClickToPayPhase>) => {
      state.clickToPayPhase = action.payload;
    },
    setClickToPayMethod: (state, action: PayloadAction<ClickToPayMethod | null>) => {
      state.clickToPayMethod = action.payload;
    },
    resetClickToPay: (state) => {
      releaseClickToPay(state);
    },
    // BillingForm's own live Zod-schema validity (see isBillingFormValid above).
    setIsBillingFormValid: (state, action: PayloadAction<boolean>) => {
      state.isBillingFormValid = action.payload;
    },

    // Gift Card Notification Actions
    setGiftCardNotification: (state, action: PayloadAction<Partial<GiftCardNotification>>) => {
      state.giftCardNotification = {
        ...state.giftCardNotification,
        ...action.payload,
      };
    },
    resetGiftCardNotification: (state) => {
      state.giftCardNotification = initialGiftCardNotification;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state) => {
      state.shouldShowPaymentDetails = false;
      releaseClickToPay(state);
    });
  },
});

// Hooks
export const usePaymentState = () => useAppSelector((state) => state.payment);
export const useContactInfo = () => useAppSelector((state) => state.payment.contactInfo);
export const usePaymentMethod = () => useAppSelector((state) => state.payment.paymentMethod);
export const useCreditCardInfo = () => useAppSelector((state) => state.payment.creditCardInfo);
export const useBillingInfo = () => useAppSelector((state) => state.payment.billingInfo);
export const useDocumentSelection = () =>
  useAppSelector((state) => state.payment.documentSelection);
export const useTaxInformation = () => useAppSelector((state) => state.payment.taxInformation);
export const useTermsAndConditions = () =>
  useAppSelector((state) => state.payment.termsAndConditions);
export const useGiftCardInfo = () => useAppSelector((state) => state.payment.giftCardInfo);
export const usePaymentConfirmed = () => useAppSelector((state) => state.payment.confirmed);
export const useContactErrorMessage = () =>
  useAppSelector((state) => state.payment.contactErrorMessage ?? null);
export const usePaymentPNR = () => useAppSelector((state) => state.payment.pnr);
// `?? false` covers tab state persisted before this field existed
export const useIsPaymentRetry = () =>
  useAppSelector((state) => state.payment.isPaymentRetry ?? false);
export const useYunoPaymentStatus = () =>
  useAppSelector((state) => state.payment.yunoPaymentStatus);
export const useShowFinalizationModal = () =>
  useAppSelector((state) => state.payment.showFinalizationModal);
export const useShouldShowPaymentDetails = () =>
  useAppSelector((state) => state.payment.shouldShowPaymentDetails);

// Gift Card Payment State Hooks
export const useIsGiftCardFullPayment = () =>
  useAppSelector((state) => state.payment.isGiftCardFullPayment);
export const useNewBalanceDue = () => useAppSelector((state) => state.payment.newBalanceDue);
export const useHasUsedGiftCardInReservation = () =>
  useAppSelector((state) => state.payment.hasUsedGiftCardInReservation);
export const useGiftCardNotification = () =>
  useAppSelector((state) => state.payment.giftCardNotification);
// Wallet State Hooks
export const useWalletInfo = () => useAppSelector((state) => state.payment.walletInfo);
export const useIsWalletFullPayment = () =>
  useAppSelector((state) => state.payment.walletInfo.isWalletFullPayment);

// Click to Pay State Hooks
export const useClickToPayPhase = () => useAppSelector((state) => state.payment.clickToPayPhase);
export const useClickToPayMethod = () => useAppSelector((state) => state.payment.clickToPayMethod);
export const useIsBillingFormValid = () =>
  useAppSelector((state) => state.payment.isBillingFormValid);

export const useIsValidBillingInfo = () => {
  const isValid = useAppSelector((state) => {
    if (!state.payment.documentSelection.invoiceChecked) return true; // Not required if invoice not checked
    return (
      state.payment.billingInfo.rut.trim() !== "" &&
      state.payment.billingInfo.companyName.trim() !== "" &&
      state.payment.billingInfo.address.trim() !== "" &&
      state.payment.billingInfo.city.trim() !== "" &&
      state.payment.billingInfo.email.trim() !== ""
    );
  });

  return isValid;
};

// Utility hooks
export const useIsPaymentValid = () => {
  const paymentState = usePaymentState();
  return validatePayment(paymentState);
};

// Expose a lightweight selector for contact completeness (used to gate Gift Card add)
export const useIsContactInfoComplete = () => {
  const contact = useAppSelector((state) => state.payment.contactInfo);
  return (
    contact.firstName.trim() !== "" &&
    contact.lastName.trim() !== "" &&
    contact.email.trim() !== "" &&
    contact.phoneNumber.trim() !== ""
    // && availablePrefix.includes(contact.phoneNumberPrefix ?? "")
  );
};

export const usePaymentValidationErrors = () => {
  const paymentState = usePaymentState();
  const errors: string[] = [];

  if (!validateContactInfo(paymentState.contactInfo)) {
    errors.push("Contact information is incomplete");
  }
  if (!paymentState.paymentMethod) {
    errors.push("Payment method is not selected");
  }
  if (
    paymentState.paymentMethod === "credit-debit" &&
    !validateCreditCardInfo(paymentState.creditCardInfo)
  ) {
    errors.push("Credit card information is incomplete");
  }
  if (
    paymentState.documentSelection.invoiceChecked &&
    !validateBillingInfo(paymentState.billingInfo, true)
  ) {
    errors.push("Billing information is incomplete");
  }
  if (!paymentState.termsAndConditions.accepted) {
    errors.push("Terms and conditions must be accepted");
  }
  if (
    !paymentState.documentSelection.receiptChecked &&
    !paymentState.documentSelection.invoiceChecked
  ) {
    errors.push("Document type must be selected");
  }

  return errors;
};

export const {
  updateContactInfo,
  setContactInfo,
  setPaymentMethod,
  clearPaymentMethod,
  updateCreditCardInfo,
  setCreditCardInfo,
  updateBillingInfo,
  setBillingInfo,
  setIsBillingFormValid,
  updateTaxInformation,
  setTaxInformation,
  setDocumentSelection,
  setReceiptChecked,
  setInvoiceChecked,
  setTermsAccepted,
  setContactErrorMessage,
  setGiftCardCode,
  setGiftCardData,
  toggleGiftCard,
  removeGiftCard,
  // Gift Card Payment Actions
  applyGiftCardPayment,
  setHasUsedGiftCardInReservation,
  // Other Actions
  confirmPayment,
  resetPaymentConfirmation,
  validatePaymentAction,
  resetPayment,
  setPNR,
  setIsPaymentRetry,
  setShouldShowPaymentDetails,
  setYunoPaymentStatus,
  setShowFinalizationModal,
  // Gift Card Notification Actions
  setGiftCardNotification,
  resetGiftCardNotification,
  // Wallet Actions
  setAppliedWalletAmountInLocalCurrency,
  setWalletIsApplied,
  setNewBalanceDue,
  setWalletFullPayment,
  // Click to Pay Deferred Mount Actions
  setClickToPayPhase,
  setClickToPayMethod,
  resetClickToPay,
} = paymentSlice.actions;

export default paymentSlice.reducer;
